import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { StockItemRef } from '@/lib/recipeEditor'

/**
 * The stock items a recipe can draw on, under the admin+manager gate.
 *
 * ONE query key, ONE queryFn shape (the array), shared by the product form,
 * the Costing & Recipes sheet and the option dialog. The F-FE-N6 rule: two
 * components writing the same key with different shapes white-screen whichever
 * resolves second. AddStockItemModal invalidates this key on create.
 */
export const STOCK_ITEM_OPTIONS_QUERY_KEY = ['admin-stock-item-options'] as const

export interface StockItemOption {
  id: string
  name: string
  unit: string
  quantity_on_hand: number
  supplier_id?: string | null
  supplier_name?: string
  unit_cost?: number | null
  /** unit_cost is a moving average of costed receipts (handlers.CostedReceiptExistsSQL); a typed cost does not change it. */
  cost_from_receipts?: boolean
}

/** The one queryFn for STOCK_ITEM_OPTIONS_QUERY_KEY, reused by fetchQuery so the cached shape never differs (F-FE-N6). */
export const fetchStockItemOptions = () =>
  apiClient.getStockItemOptions().then((res) => (res.data ?? []) as StockItemOption[])

export function useStockItemOptions() {
  return useQuery({
    queryKey: STOCK_ITEM_OPTIONS_QUERY_KEY,
    queryFn: fetchStockItemOptions,
    staleTime: 30_000,
  })
}

/** The editor's view of the same list, keyed by id. */
export function stockItemRefs(options: StockItemOption[] | undefined): Record<string, StockItemRef> {
  const out: Record<string, StockItemRef> = {}
  for (const o of options ?? []) {
    out[o.id] = {
      id: o.id,
      name: o.name,
      unit: o.unit,
      unitCost: o.unit_cost != null && o.unit_cost > 0 ? o.unit_cost : null,
      supplierName: o.supplier_name,
      costFromReceipts: Boolean(o.cost_from_receipts),
    }
  }
  return out
}
